'use client';

import {Collapsible, CollapsibleContent} from '@radix-ui/react-collapsible';
import React, {useState} from 'react';

import {ChevronRightIcon} from 'lucide-react';
import {cn} from '../lib/utils';
import {CollapsibleTrigger} from './collapsible';

const DROPDOWN_ICON_PLACEHOLDER_WIDTH = '18px';

export type TreeNodeData<T> = {
  key: string;
  object: T;
  children?: TreeNodeData<T>[];
  isInitialOpen?: boolean;
};

type TreeProps<T> = {
  className?: string;
  treeData: TreeNodeData<T>;
  renderNode: TreeNodeProps<T>['renderNode'];
};

/**
 * Component that renders a generic tree.
 * @param treeData - The tree data.
 * @param renderNode - A function that renders a tree node.
 */
export function Tree<T>(props: TreeProps<T>): React.ReactElement {
  const {className, treeData, renderNode} = props;
  return (
    <div className={cn('flex flex-col', className)}>
      <TreeNode<T> treeData={treeData} renderNode={renderNode} />
    </div>
  );
}

type TreeNodeProps<T> = {
  treeData: TreeNodeData<T>;
  renderNode: (node: TreeNodeData<T>, isOpen: boolean) => React.ReactNode;
};

/**
 * Component that renders a tree node.
 */
function TreeNode<T>(props: TreeNodeProps<T>): React.ReactElement | null {
  const {treeData, renderNode} = props;
  const {children} = treeData;
  const hasChildren = Boolean(children?.length);
  const [isOpen, setIsOpen] = useState(Boolean(treeData.isInitialOpen));
  if (!hasChildren) {
    return (
      <div className="flex w-full items-center space-x-1">
        <div
          className="mr-px ml-2 shrink-0"
          style={{width: DROPDOWN_ICON_PLACEHOLDER_WIDTH}}
        />
        {renderNode(treeData, isOpen)}
      </div>
    );
  }
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full" asChild>
        <div className="flex w-full cursor-pointer items-center space-x-1">
          <ChevronRightIcon
            className={cn('mr-px ml-2 shrink-0 text-gray-500', {
              'rotate-90 transform': isOpen,
            })}
            size={DROPDOWN_ICON_PLACEHOLDER_WIDTH}
          />
          {renderNode(treeData, isOpen)}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-2">
        {isOpen
          ? children?.map((child) => (
              <TreeNode<T>
                key={child.key}
                treeData={child}
                renderNode={renderNode}
              />
            ))
          : null}
      </CollapsibleContent>
    </Collapsible>
  );
}
